import {
  DatatypeOperator,
  Direction,
  ObjectOperator,
  ObjectOperatorType,
  OperatorType,
  Variable,
} from 'vet-bones/utils/queryBuilder';

import {
  RDF_TYPE_IRI,
  XSD_BOOLEAN_IRI,
  XSD_DATE_IRI,
  XSD_DATE_TIME_IRI,
  XSD_DECIMAL_IRI,
  XSD_DOUBLE_IRI,
  XSD_FLOAT_IRI,
  XSD_INTEGER_IRI,
  XSD_STRING_IRI,
  XSD_TIME_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { OBJECT_VARIABLE } from 'src/utils/stringify/rules/constants';

export const buildThenObjectFilter = ({
  abbreviate,
  objectIri,
  objectVariable,
  operator,
  subjectIri,
  subjectVariable,
  value,
}: {
  abbreviate: (iri: string) => string;
  objectIri: string;
  objectVariable: string;
  operator: ObjectOperator;
  subjectIri: string;
  subjectVariable: string;
  value: Variable | string;
}): string => {
  const { direction, iri, type } = operator;
  const isOutbound = direction === Direction.OUTBOUND;

  const subject = subjectIri ? abbreviate(subjectIri) : subjectVariable;
  const object = objectIri ? abbreviate(objectIri) : objectVariable;

  switch (type) {
    case ObjectOperatorType.CONNECTED: {
      if (iri === RDF_TYPE_IRI && typeof value === 'object') {
        return `${subject} ${abbreviate(iri)} ${abbreviate(value.iri)} .`;
      }

      return isOutbound
        ? `${subject} ${abbreviate(iri)} ${object} .`
        : `${object} ${abbreviate(iri)} ${subject} .`;
    }

    default:
      return '';
  }
};

export const buildThenDatatypeFilter = ({
  abbreviate,
  operator,
  subjectIri,
  subjectVariable,
  value,
}: {
  abbreviate: (iri: string) => string;
  operator: DatatypeOperator;
  subjectIri: string;
  subjectVariable: string;
  value: string;
}): string => {
  const { iri, range } = operator;
  const subject = subjectIri ? abbreviate(subjectIri) : subjectVariable;

  let typedValue = '';
  const rangeIri = abbreviate(range || XSD_STRING_IRI);
  switch (range) {
    case null:
    case XSD_STRING_IRI: {
      typedValue = `"${escapeDoubleQuotes(value)}"`;
      break;
    }
    case XSD_BOOLEAN_IRI: {
      typedValue = value;
      break;
    }

    case XSD_DECIMAL_IRI:
    case XSD_DOUBLE_IRI:
    case XSD_FLOAT_IRI:
    case XSD_INTEGER_IRI:
    case XSD_DATE_TIME_IRI:
    case XSD_DATE_IRI:
    case XSD_TIME_IRI: {
      typedValue = `"${escapeDoubleQuotes(value)}"^^${rangeIri})`;
      break;
    }

    default:
      break;
  }

  if (!typedValue) {
    return '';
  }

  return `${subject} ${abbreviate(iri)} ${typedValue} .`;
};

export const buildThenFilter = ({
  abbreviate,
  getInstanceIri,
  getVariable,
  operator,
  subject,
  value,
}: {
  abbreviate: (iri: string) => string;
  getInstanceIri: (subject: Variable) => string;
  getVariable: (variable: string, id?: string) => string;
  operator: DatatypeOperator | ObjectOperator;
  subject: Variable;
  value: Variable | string;
}): string => {
  const subjectIri = getInstanceIri(subject) || '';
  const subjectVariable = getVariable(OBJECT_VARIABLE, subject.id);

  // no instance operator type for then filters
  if (operator.operatorType === OperatorType.DATATYPE) {
    return buildThenDatatypeFilter({
      abbreviate,
      operator,
      subjectIri,
      subjectVariable,
      value: value as string,
    });
  }

  const objectValue = value as Variable;
  const objectIri = getInstanceIri(objectValue) || '';
  const objectVariable = getVariable(OBJECT_VARIABLE, objectValue.id);

  return buildThenObjectFilter({
    abbreviate,
    objectIri,
    objectVariable,
    operator,
    subjectIri,
    subjectVariable,
    value,
  });
};
